import { IdPrefix } from '@monetize/types/common';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';
import { apiGet } from '../api/axios';
import {
  accountServiceQueryKeys,
  contactServiceQueryKeys,
  subscriptionServiceQueryKeys,
} from '../api/queryKeysService';
import { MPageLoader } from '../components/Monetize';
import {
  getAccountContactView,
  getAccountCreditNoteViewRoute,
  getAccountCreditsViewRoute,
  getAccountDetailRoute,
  getAccountDocumentDetails,
  getAccountPaymentView,
  getBillGroupPageRoute,
  getDiscountEditRoute,
  getInvoiceDetailRoute,
  getOfferingEditRoute,
  getProductEditRoute,
  getQuoteBaseRoute,
  getRefundView,
  getSalesContractEditRoute,
  getSalesOpportunityEditRoute,
  getSubscriptionOverviewRoute,
  getUsageTypeEditRoute,
} from '../constants/routes';
import { useAuth } from '../services/auth0';
import EmptyLogin from './Auth/EmptyLogin';
import NotFound from './NotFound';

type IdPrefixRouteFn = (
  entityId: string,
  entityData?: Record<string, unknown>,
) => string;

interface IdPrefixConfig {
  routeFunc: IdPrefixRouteFn;
  /**
   * API endpoint to fetch entity data. When present, indicates that this entity
   * requires data fetching before routing (e.g., to get accountId for nested routes).
   * If undefined, the entity can be routed to directly without additional data.
   */
  apiEndpoint?: string;
  /**
   * Query key generator for React Query caching. Should be provided when apiEndpoint
   * is present to ensure proper cache management and prevent duplicate API calls.
   */
  getQueryKey?: (entityId: string) => readonly unknown[];
}

export const IdPrefixConfigMap: Record<IdPrefix, IdPrefixConfig> = {
  acct: {
    routeFunc: (entityId) => getAccountDetailRoute(entityId),
  },
  acdoc: {
    apiEndpoint: '/api/accountDocuments',
    routeFunc: (entityId, entityData) => {
      const accountId = getAccountId(entityData);
      return getAccountDocumentDetails(accountId, entityId);
    },
    getQueryKey: (entityId) =>
      accountServiceQueryKeys.accountDocuments.accountDocumentById(entityId),
  },
  ctrct: {
    routeFunc: (entityId) => getSalesContractEditRoute(entityId),
  },
  disc: {
    routeFunc: (entityId) => getDiscountEditRoute(entityId),
  },
  disct: {
    // Legacy prefix - 'disc' is being used instead
    routeFunc: (entityId) => getDiscountEditRoute(entityId),
  },
  invc: {
    routeFunc: (entityId) => getInvoiceDetailRoute(entityId),
  },
  invce: {
    // Legacy prefix - 'invc' is being used instead
    routeFunc: (entityId) => getInvoiceDetailRoute(entityId),
  },
  offr: {
    routeFunc: (entityId) => getOfferingEditRoute(entityId),
  },
  offer: {
    // Legacy prefix - 'offr' is being used instead
    routeFunc: (entityId) => getOfferingEditRoute(entityId),
  },
  opp: {
    routeFunc: (entityId) => getSalesOpportunityEditRoute(entityId),
  },
  prod: {
    routeFunc: (entityId) => getProductEditRoute(entityId),
  },
  quot: {
    routeFunc: (entityId) => getQuoteBaseRoute(entityId),
  },
  usgt: {
    routeFunc: (entityId) => getUsageTypeEditRoute(entityId),
  },
  crdt: {
    apiEndpoint: '/api/credits',
    routeFunc: (entityId, entityData) => {
      const accountId = getAccountId(entityData);
      return getAccountCreditsViewRoute(accountId, entityId);
    },
    getQueryKey: (entityId) =>
      accountServiceQueryKeys.credits.creditDetail(entityId),
  },
  crnt: {
    apiEndpoint: '/api/creditNotes',
    routeFunc: (entityId, entityData) => {
      const accountId = getAccountId(entityData);
      return getAccountCreditNoteViewRoute(accountId, entityId);
    },
    getQueryKey: (entityId) =>
      accountServiceQueryKeys.creditNotes.creditNoteById(entityId),
  },
  pymt: {
    apiEndpoint: '/api/payments',
    routeFunc: (entityId, entityData) => {
      const accountId = getAccountId(entityData);
      return getAccountPaymentView(accountId, entityId);
    },
    getQueryKey: (entityId) =>
      accountServiceQueryKeys.payments.paymentById(entityId),
  },
  rfnd: {
    apiEndpoint: '/api/refunds',
    routeFunc: (entityId, entityData) => {
      const accountId = getAccountId(entityData);
      return getRefundView({ accountId, refundId: entityId });
    },
    getQueryKey: (entityId) =>
      accountServiceQueryKeys.refunds.refundById(entityId),
  },
  pmmd: {
    apiEndpoint: '/api/paymentMethods',
    routeFunc: (entityId, entityData) => {
      const accountId = getAccountId(entityData);
      return getAccountPaymentView(accountId, entityId);
    },
    getQueryKey: (entityId) =>
      accountServiceQueryKeys.paymentMethods.paymentMethod(entityId),
  },
  subs: {
    apiEndpoint: '/api/subscriptions',
    routeFunc: (entityId, entityData) => {
      const accountId = getAccountId(entityData);
      return getSubscriptionOverviewRoute(accountId, entityId);
    },
    getQueryKey: (entityId) =>
      subscriptionServiceQueryKeys.subscriptionDetail(entityId),
  },
  bilg: {
    apiEndpoint: '/api/billGroups',
    routeFunc: (entityId) => {
      return getBillGroupPageRoute(entityId, 'details');
    },
    getQueryKey: (entityId) =>
      accountServiceQueryKeys.billGroups.billGroupById(entityId),
  },
  cont: {
    apiEndpoint: '/api/contacts',
    routeFunc: (entityId, entityData) => {
      const accountId = getAccountId(entityData);
      return getAccountContactView(accountId, entityId);
    },
    getQueryKey: (entityId) => contactServiceQueryKeys.contactDetail(entityId),
  },
};

const getIdPrefix = (pathname: string) => {
  return pathname.replace('/', '').split('_')[0] as IdPrefix;
};

const getAccountId = (entityData?: Record<string, unknown>): string => {
  if (!entityData) {
    return '';
  }

  if (typeof entityData.accountId === 'string') {
    return entityData.accountId;
  }

  if (entityData.account && typeof entityData.account === 'object') {
    const account = entityData.account as Record<string, unknown>;
    if (typeof account.id === 'string') {
      return account.id;
    }
  }

  return '';
};

/*
 * we want to be able to visit https://app.monetizeplatform.com/offer_TSDNkMIiKL2KGSRR and this component automatically
 * redirected to https://app.monetizeplatform.com/offerings/offer_TSDNkMIiKL2KGSRR/edit
 * TICKET: https://monetizenow.atlassian.net/browse/BP-7119
 */
const IdPrefixNavigator = () => {
  const { pathname } = useLocation();
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();

  const prefix = getIdPrefix(pathname);
  const entityId = pathname.replace('/', '');
  const config = IdPrefixConfigMap[prefix];

  const {
    data: entityData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: config?.getQueryKey
      ? config.getQueryKey(entityId)
      : ['entity-details', prefix, entityId],
    queryFn: () =>
      apiGet(`${config.apiEndpoint}/${entityId}`).then(({ data }) => data),
    enabled: !!config?.apiEndpoint && isAuthenticated,
  });

  if (isAuthLoading) {
    return <MPageLoader />;
  }

  if (!isAuthenticated) {
    return <EmptyLogin />;
  }

  if (config?.apiEndpoint && isLoading) {
    return <MPageLoader />;
  }

  // Error state if data fetching failed or unknown prefix
  if (!config || (config.apiEndpoint && isError)) {
    return <NotFound />;
  }

  const redirectTo = config.routeFunc(entityId, entityData);

  return <Navigate to={redirectTo} replace />;
};

export default IdPrefixNavigator;
